import { Setter } from "../services/ComService"
import { parseStringFromServer } from "../services/TextConverter"
import { GLItemDTO, GLItemKind, GLItemStatus, GLItemStatuses, IGLItem, weightedPotential } from "./GLItemDTO"

export type SalesChartMode = 'company' | 'project' | 'compare'
export type SalesChartGroupBy = 'quarter' | 'year' | 'freshness' | 'wonLost'
export type SalesChartLayout = 'graph' | 'list'
export type SalesChartFreshnessKey = 'moldy' | 'expired' | 'overripe' | 'ripe' | 'fresh'

export const SALES_PIPELINE_STATUSES: GLItemStatus[] = [
    GLItemStatus.generic,
    GLItemStatus.marketingContacted,
    GLItemStatus.marketingOrderDiscussed,
    GLItemStatus.marketingOrderSend,
    GLItemStatus.marketingWaitingForFeedback,
]

export const SALES_FRESHNESS_BUCKETS: Array<{
    key: SalesChartFreshnessKey
    label: string
    minWeeks: number
}> = [
    { key: 'moldy', label: 'Schimmlig', minWeeks: 12 },
    { key: 'expired', label: 'Abgelaufen', minWeeks: 8 },
    { key: 'overripe', label: 'Überreif', minWeeks: 4 },
    { key: 'ripe', label: 'Reif', minWeeks: 2 },
    { key: 'fresh', label: 'Frisch', minWeeks: 0 },
]

export type GLItemStatusHistoryPoint = {
    status: GLItemStatus
    date?: string
}

export type GLItemChartRow = {
    id: number
    headline: string
    status: GLItemStatus
    startdate: string
    created?: string
    manualdate?: string
    revenue_potential?: number
    revenue_probability?: number
    customerId: number
    customerName: string
    personId?: number
    personName?: string
    statusHistory?: GLItemStatusHistoryPoint[]
}

export type SalesRankingRow = {
    id: string
    name: string
    value: number
}

export type SalesChartListRow = {
    id: number
    headline: string
    customerName: string
    personName: string
    status: GLItemStatus
    quarterLabel: string
    value: number
    groupKey?: string
    itemCount?: number
}

export type ChartBarItemFilter = {
    groupBy: SalesChartGroupBy
    statuses?: GLItemStatus[]
    xKey?: string
    seriesKey?: string
    yearCount?: number
    now?: Date
}

export type ChartRowDisplayFilter = {
    status?: GLItemStatus
    statuses?: GLItemStatus[]
    requireQuarter?: boolean
    requireYear?: boolean
    requireThreadDate?: boolean
}

export type SalesChartSeries = {
    key: string
    label: string
    color: string
    itemId: number
    customerId: number
    status: GLItemStatus
    customerName: string
    headline: string
}

export type SalesChartQuarter = {
    key: string
    year: number
    quarter: string
    label: string
}

export type SalesChartFilterOption = {
    id: string
    name: string
}

export type SalesChartPoint = {
    xKey: string
    quarterKey?: string
    year?: number
    quarter?: string
    label: string
    total?: number
    [seriesKey: string]: number | string | undefined
}

export type SalesChartStack = {
    points: SalesChartPoint[]
    series: SalesChartSeries[]
    quarters: SalesChartQuarter[]
    filterOptions: SalesChartFilterOption[]
}

const QUARTERS = ['Q1', 'Q2', 'Q3', 'Q4']

function colorFromKey(key: string): string {
    let hash = 0
    for (let i = 0; i < key.length; i++) {
        hash = key.charCodeAt(i) + ((hash << 5) - hash)
    }
    const hue = Math.abs(hash) % 360
    return `hsl(${hue}, 55%, 48%)`
}

function quarterIndex(quarter: string): number {
    const i = QUARTERS.indexOf(quarter)
    return i < 0 ? 0 : i
}

function quarterKey(year: number, quarter: string): string {
    return `${year}-${quarter}`
}

const MS_PER_WEEK = 7 * 24 * 60 * 60 * 1000

export function chartRowThreadDate(row: GLItemChartRow): Date | null {
    const raw = row.manualdate || row.created
    if (!raw) {
        return null
    }
    const date = new Date(raw)
    if (Number.isNaN(date.getTime())) {
        return null
    }
    return date
}

export function chartRowYear(row: GLItemChartRow): number | null {
    const quarter = GLItemDTO.quarterFromStartdate(row.startdate)
    if (quarter) {
        return quarter.year
    }
    const threadDate = chartRowThreadDate(row)
    return threadDate ? threadDate.getFullYear() : null
}

export function chartRowHistoryYear(row: GLItemChartRow, statuses: GLItemStatus[]): number | null {
    const entries = row.statusHistory || []
    for (let i = 0; i < entries.length; i++) {
        const entry = entries[i]
        if (statuses.indexOf(entry.status) < 0 || !entry.date) {
            continue
        }
        const date = new Date(entry.date)
        if (!Number.isNaN(date.getTime())) {
            return date.getFullYear()
        }
    }
    return null
}

function lastCalendarYears(count: number): number[] {
    const now = new Date().getFullYear()
    const years: number[] = []
    for (let year = now - (count - 1); year <= now; year++) {
        years.push(year)
    }
    return years
}

export function freshnessKeyFromWeeks(weeks: number): SalesChartFreshnessKey {
    for (const bucket of SALES_FRESHNESS_BUCKETS) {
        if (bucket.key === 'fresh' || weeks > bucket.minWeeks) {
            return bucket.key
        }
    }
    return 'fresh'
}

function hydrateChartRow(raw: any): GLItemChartRow {
    return {
        id: raw.id,
        headline: parseStringFromServer(raw.headline || ''),
        status: raw.status,
        startdate: raw.startdate || '',
        created: raw.created || '',
        manualdate: raw.manualdate || '',
        revenue_potential: raw.revenue_potential,
        revenue_probability: raw.revenue_probability,
        customerId: raw.customerId || 0,
        customerName: parseStringFromServer(raw.customerName || ''),
        personId: raw.personId || 0,
        personName: parseStringFromServer(raw.personName || ''),
        statusHistory: Array.isArray(raw.statusHistory)
            ? raw.statusHistory.map((entry: any) => ({
                status: entry.status,
                date: entry.date || '',
            }))
            : [],
    }
}

function matchesChartStatus(row: GLItemChartRow, filter?: ChartRowDisplayFilter): boolean {
    if (filter?.status !== undefined) {
        return row.status === filter.status
    }
    if (filter?.statuses && filter.statuses.length > 0) {
        return filter.statuses.indexOf(row.status) >= 0
    }
    return true
}

function emptyChartStack(): SalesChartStack {
    return { points: [], series: [], quarters: [], filterOptions: [] }
}

function chartBarHistoryStatuses(filter: ChartBarItemFilter): GLItemStatus[] {
    if (filter.groupBy === 'wonLost') {
        if (filter.seriesKey === 'won') {
            return [GLItemStatus.marketingOrderWon]
        }
        if (filter.seriesKey === 'lost') {
            return [GLItemStatus.marketingOrderLost]
        }
        if (filter.statuses && filter.statuses.length > 0) {
            return filter.statuses
        }
        return [GLItemStatus.marketingOrderWon, GLItemStatus.marketingOrderLost]
    }
    return filter.statuses || []
}

function chartItemPeriodLabel(
    row: GLItemChartRow,
    groupBy: SalesChartGroupBy,
    statuses: GLItemStatus[] | undefined,
    seriesKey: string | undefined,
    now: Date,
): string {
    if (groupBy === 'freshness') {
        const threadDate = chartRowThreadDate(row)
        if (!threadDate) {
            return ''
        }
        const weeks = (now.getTime() - threadDate.getTime()) / MS_PER_WEEK
        const key = freshnessKeyFromWeeks(weeks)
        const bucket = SALES_FRESHNESS_BUCKETS.find(entry => entry.key === key)
        return bucket?.label || ''
    }
    if (groupBy === 'year' || groupBy === 'wonLost') {
        const historyStatuses = chartBarHistoryStatuses({
            groupBy,
            statuses,
            seriesKey,
        })
        const year = groupBy === 'wonLost' && !seriesKey
            ? chartRowHistoryYear(row, [GLItemStatus.marketingOrderWon])
                ?? chartRowHistoryYear(row, [GLItemStatus.marketingOrderLost])
            : chartRowHistoryYear(row, historyStatuses)
        return year === null ? '' : String(year)
    }
    const q = GLItemDTO.quarterFromStartdate(row.startdate)
    return q ? `${q.year} ${q.quarter}` : ''
}

function listRowFromChartItem(
    row: GLItemChartRow,
    groupBy: SalesChartGroupBy,
    statuses: GLItemStatus[] | undefined,
    seriesKey: string | undefined,
    now: Date,
): SalesChartListRow {
    return {
        id: row.id,
        headline: row.headline || '(ohne Titel)',
        customerName: row.customerName || 'Ohne Firma',
        personName: row.personName || 'Ohne Person',
        status: row.status,
        quarterLabel: chartItemPeriodLabel(row, groupBy, statuses, seriesKey, now),
        value: weightedPotential(row.revenue_potential, row.revenue_probability),
        groupKey: String(row.customerId || 0),
        itemCount: 1,
    }
}

export interface GLItemRequest {
    id?: number
    page?: number
    limit?: number
    personId?: number
    customerId?: number
    needle?: string
    kind?: GLItemKind
    status?: GLItemStatus
    userId?: number
    pastduedate?: boolean
    newsworthy?: boolean
}
export class GLItemsDTO {
    request: GLItemRequest
    items: GLItemDTO[] = []
    allowInfinitScroll: boolean = true
    total: number = 0
    statusCounts: Record<string, number> = {}
    statusCountsAll: number = 0
    chartItems: GLItemChartRow[] = []

    constructor(data: GLItemRequest) {
        this.request = data
    }

    patchRequest(data: GLItemRequest) {
        this.request = {...this.request, ...data}
    }

    async getStatusCountsFromServer(data?: GLItemRequest) {
        if (data) this.patchRequest(data)
        const result = await Setter('spt/gl_items/status_counts', {
            kind: this.request.kind,
            userId: this.request.userId,
            pastduedate: this.request.pastduedate,
        }) as { counts?: Record<string, number>, all?: number }
        this.statusCounts = result.counts || {}
        this.statusCountsAll = result.all || 0
        return result
    }

    countForStatus(status?: GLItemStatus) {
        if (status === undefined) {
            return this.statusCountsAll
        }
        return this.statusCounts[String(status)] || 0
    }

    async getFromServer(data?: GLItemRequest) {
        this.allowInfinitScroll = false
        if(data) this.patchRequest(data)
        const result = await Setter('spt/gl_items/search', this.request)
        const newItems = result.data.map((item: any) => new GLItemDTO(item))
        this.total = result.total
        if (newItems.length === 0) {
            return
        }
        this.allowInfinitScroll = true
        if (this.request.page === 1) {
            this.items = newItems
        } else {
            this.items = [...this.items, ...newItems]
        }
    }

    async getNextPage() {
        if (!this.allowInfinitScroll) { return }
        await this.getFromServer({page: (this.request.page || 1) + 1})
    }

    async getFresh() {
        this.items = []
        this.allowInfinitScroll = true
        await this.getFromServer({page: 1})
    }

    /** Replace-mode page load (for classic pagination, not infinite scroll). */
    async getPage(page: number) {
        this.allowInfinitScroll = false
        this.request.page = page
        const result = await Setter('spt/gl_items/search', this.request)
        this.items = result.data.map((item: any) => new GLItemDTO(item))
        this.total = result.total
    }

    async getChartFromServer(data?: { kind?: GLItemKind, status?: GLItemStatus }) {
        const payload: { kind?: GLItemKind, status?: GLItemStatus } = {
            kind: data?.kind !== undefined ? data.kind : this.request.kind,
        }
        if (data?.status !== undefined) {
            payload.status = data.status
        }
        const result = await Setter('spt/gl_items/chart', payload) as { data?: any[] } | undefined
        this.chartItems = (result?.data || []).map(hydrateChartRow)
        return this.chartItems
    }

    chartRowsForDisplay(filter?: ChartRowDisplayFilter | GLItemStatus): GLItemChartRow[] {
        const options: ChartRowDisplayFilter = typeof filter === 'object' && filter !== null
            ? filter
            : { status: filter as GLItemStatus | undefined }
        return this.chartItems.filter(row => {
            if (!matchesChartStatus(row, options)) {
                return false
            }
            if (weightedPotential(row.revenue_potential, row.revenue_probability) <= 0) {
                return false
            }
            if (options.requireQuarter) {
                if (!row.startdate) {
                    return false
                }
                const quarter = GLItemDTO.quarterFromStartdate(row.startdate)
                if (!quarter || quarter.quarter === 'none') {
                    return false
                }
            }
            if (options.requireYear && chartRowYear(row) === null) {
                return false
            }
            if (options.requireThreadDate && !chartRowThreadDate(row)) {
                return false
            }
            return true
        })
    }

    buildStackedChart(
        mode: SalesChartMode,
        status?: GLItemStatus,
        filterId?: string,
        statuses?: GLItemStatus[],
    ): SalesChartStack {
        const statusRows = this.chartRowsForDisplay({ status, statuses, requireQuarter: true })
        const rows = statusRows.filter(row => {
            if (!filterId) {
                return true
            }
            if (mode === 'company') {
                return String(row.customerId || 0) === filterId
            }
            return String(row.id) === filterId
        })

        const filterOptions = this.chartFilterOptions(mode, status, statuses)
        const quarters = this.chartQuarterRaster(statusRows)
        const seriesMap = new Map<string, SalesChartSeries>()
        const values = new Map<string, Map<string, number>>()
        const totals = new Map<string, number>()

        rows.forEach(row => {
            const q = GLItemDTO.quarterFromStartdate(row.startdate)
            if (!q) {
                return
            }
            const qKey = quarterKey(q.year, q.quarter)
            const weight = weightedPotential(row.revenue_potential, row.revenue_probability)
            const seriesKey = mode === 'company'
                ? `c-${row.customerId || 0}`
                : `p-${row.id}`
            const customerName = row.customerName || 'Ohne Firma'
            const headline = row.headline || '(ohne Titel)'
            const label = mode === 'company'
                ? customerName
                : `${customerName} – ${headline}`

            if (!seriesMap.has(seriesKey)) {
                seriesMap.set(seriesKey, {
                    key: seriesKey,
                    label,
                    color: colorFromKey(seriesKey),
                    itemId: row.id,
                    customerId: row.customerId || 0,
                    status: row.status,
                    customerName,
                    headline,
                })
            }

            if (!values.has(qKey)) {
                values.set(qKey, new Map())
            }
            const bucket = values.get(qKey)!
            bucket.set(seriesKey, (bucket.get(seriesKey) || 0) + weight)
            totals.set(seriesKey, (totals.get(seriesKey) || 0) + weight)
        })

        const series = Array.from(seriesMap.values()).sort((a, b) => {
            return (totals.get(b.key) || 0) - (totals.get(a.key) || 0)
        })

        const points: SalesChartPoint[] = quarters.map(q => {
            const point: SalesChartPoint = {
                xKey: q.key,
                quarterKey: q.key,
                year: q.year,
                quarter: q.quarter,
                label: q.label,
            }
            const bucket = values.get(q.key)
            let total = 0
            series.forEach(s => {
                const value = bucket?.get(s.key) || 0
                point[s.key] = value
                total += value
            })
            point.total = total
            return point
        })

        return { points, series, quarters, filterOptions }
    }

    buildYearlyChart(statuses: GLItemStatus[], seriesKey = 'value', yearCount?: number): SalesChartStack {
        if (!statuses || statuses.length === 0) {
            return emptyChartStack()
        }
        const rows = this.chartItems.filter(row => {
            return weightedPotential(row.revenue_potential, row.revenue_probability) > 0
                && chartRowHistoryYear(row, statuses) !== null
        })
        if (rows.length === 0 && !yearCount) {
            return emptyChartStack()
        }
        const years = yearCount
            ? lastCalendarYears(yearCount)
            : this.chartYearRasterFromHistory(rows, statuses)
        if (years.length === 0) {
            return emptyChartStack()
        }
        const values = new Map<number, number>()
        rows.forEach(row => {
            const year = chartRowHistoryYear(row, statuses)
            if (year === null) {
                return
            }
            values.set(year, (values.get(year) || 0) + weightedPotential(row.revenue_potential, row.revenue_probability))
        })
        const hasValue = years.some(year => (values.get(year) || 0) > 0)
        if (!hasValue) {
            return emptyChartStack()
        }
        const series: SalesChartSeries[] = [{
            key: seriesKey,
            label: 'Gewichtetes Potenzial',
            color: colorFromKey(`year-${statuses.join('-')}`),
            itemId: 0,
            customerId: 0,
            status: statuses[0],
            customerName: '',
            headline: '',
        }]
        const points: SalesChartPoint[] = years.map(year => {
            const value = values.get(year) || 0
            return {
                xKey: String(year),
                year,
                label: String(year),
                total: value,
                [seriesKey]: value,
            }
        })
        return { points, series, quarters: [], filterOptions: [] }
    }

    buildWonLostChart(yearCount = 3): SalesChartStack {
        const years = lastCalendarYears(yearCount)
        const wonStatuses = [GLItemStatus.marketingOrderWon]
        const lostStatuses = [GLItemStatus.marketingOrderLost]
        const wonKey = 'won'
        const lostKey = 'lost'
        const points: SalesChartPoint[] = years.map(year => {
            let won = 0
            let lost = 0
            this.chartItems.forEach(row => {
                const weight = weightedPotential(row.revenue_potential, row.revenue_probability)
                if (weight <= 0) {
                    return
                }
                if (chartRowHistoryYear(row, wonStatuses) === year) {
                    won += weight
                }
                if (chartRowHistoryYear(row, lostStatuses) === year) {
                    lost += weight
                }
            })
            return {
                xKey: String(year),
                year,
                label: String(year),
                total: won + lost,
                [wonKey]: won,
                [lostKey]: lost,
            }
        })
        const hasValue = points.some(p => (Number(p[wonKey]) || 0) > 0 || (Number(p[lostKey]) || 0) > 0)
        if (!hasValue) {
            return emptyChartStack()
        }
        const series: SalesChartSeries[] = [
            {
                key: wonKey,
                label: 'Gewonnen',
                color: '#111111',
                itemId: 0,
                customerId: 0,
                status: GLItemStatus.marketingOrderWon,
                customerName: '',
                headline: '',
            },
            {
                key: lostKey,
                label: 'Verloren',
                color: '#c62828',
                itemId: 0,
                customerId: 0,
                status: GLItemStatus.marketingOrderLost,
                customerName: '',
                headline: '',
            },
        ]
        return { points, series, quarters: [], filterOptions: [] }
    }

    buildCompareChart(statuses: GLItemStatus[]): SalesChartStack {
        const statusRows = this.chartRowsForDisplay({ statuses, requireQuarter: true })
        if (statusRows.length === 0) {
            return emptyChartStack()
        }
        const quarters = this.chartQuarterRaster(statusRows)
        const names = new Map<GLItemStatus, string>()
        const marketingStatuses = (GLItemStatuses[GLItemKind.marketing] || []) as Array<{ status?: GLItemStatus, name: string }>
        marketingStatuses.forEach(entry => {
            if (entry.status !== undefined) {
                names.set(entry.status, entry.name)
            }
        })
        const series: SalesChartSeries[] = (statuses || []).map(status => ({
            key: `s-${status}`,
            label: names.get(status) || `${status}`,
            color: colorFromKey(`status-${status}`),
            itemId: 0,
            customerId: 0,
            status,
            customerName: '',
            headline: '',
        }))
        const values = new Map<string, Map<string, number>>()
        statusRows.forEach(row => {
            const q = GLItemDTO.quarterFromStartdate(row.startdate)
            if (!q) {
                return
            }
            const qKey = quarterKey(q.year, q.quarter)
            const seriesKey = `s-${row.status}`
            if (!values.has(qKey)) {
                values.set(qKey, new Map())
            }
            const bucket = values.get(qKey)!
            bucket.set(seriesKey, (bucket.get(seriesKey) || 0) + weightedPotential(row.revenue_potential, row.revenue_probability))
        })
        const points: SalesChartPoint[] = quarters.map(q => {
            const point: SalesChartPoint = {
                xKey: q.key,
                quarterKey: q.key,
                year: q.year,
                quarter: q.quarter,
                label: q.label,
            }
            let total = 0
            series.forEach(s => {
                const value = values.get(q.key)?.get(s.key) || 0
                point[s.key] = value
                total += value
            })
            point.total = total
            return point
        })
        return { points, series, quarters, filterOptions: [] }
    }

    chartListRows(
        mode: SalesChartMode,
        status?: GLItemStatus,
        filterId?: string,
        statuses?: GLItemStatus[],
    ): SalesChartListRow[] {
        const rows = this.chartRowsForDisplay({ status, statuses, requireQuarter: true }).filter(row => {
            if (!filterId || mode === 'compare') {
                return true
            }
            if (mode === 'company') {
                return String(row.customerId || 0) === filterId
            }
            return String(row.id) === filterId
        })
        return rows.map(row => {
            const q = GLItemDTO.quarterFromStartdate(row.startdate)
            return {
                id: row.id,
                headline: row.headline || '(ohne Titel)',
                customerName: row.customerName || 'Ohne Firma',
                personName: row.personName || 'Ohne Person',
                status: row.status,
                quarterLabel: q ? `${q.year} ${q.quarter}` : '',
                value: weightedPotential(row.revenue_potential, row.revenue_probability),
            }
        }).sort((a, b) => b.value - a.value)
    }

    chartItemsForBar(filter: ChartBarItemFilter): GLItemChartRow[] {
        const now = filter.now || new Date()
        const allowedYears = filter.yearCount && !filter.xKey
            ? lastCalendarYears(filter.yearCount)
            : null
        if (filter.groupBy === 'freshness') {
            return this.chartRowsForDisplay({
                statuses: filter.statuses,
                requireThreadDate: true,
            }).filter(row => {
                if (!filter.xKey) {
                    return true
                }
                const threadDate = chartRowThreadDate(row)
                if (!threadDate) {
                    return false
                }
                const weeks = (now.getTime() - threadDate.getTime()) / MS_PER_WEEK
                return freshnessKeyFromWeeks(weeks) === filter.xKey
            })
        }
        const historyStatuses = chartBarHistoryStatuses(filter)
        if (historyStatuses.length === 0) {
            return []
        }
        return this.chartItems.filter(row => {
            if (weightedPotential(row.revenue_potential, row.revenue_probability) <= 0) {
                return false
            }
            if (filter.groupBy === 'wonLost' && !filter.seriesKey) {
                const wonYear = chartRowHistoryYear(row, [GLItemStatus.marketingOrderWon])
                const lostYear = chartRowHistoryYear(row, [GLItemStatus.marketingOrderLost])
                const years = [wonYear, lostYear].filter((year): year is number => year !== null)
                if (years.length === 0) {
                    return false
                }
                if (filter.xKey && years.every(year => String(year) !== filter.xKey)) {
                    return false
                }
                if (allowedYears && years.every(year => allowedYears.indexOf(year) < 0)) {
                    return false
                }
                return true
            }
            const year = chartRowHistoryYear(row, historyStatuses)
            if (year === null) {
                return false
            }
            if (filter.xKey && String(year) !== filter.xKey) {
                return false
            }
            if (allowedYears && allowedYears.indexOf(year) < 0) {
                return false
            }
            return true
        })
    }

    chartListRowsForGroup(
        groupBy: SalesChartGroupBy,
        statuses: GLItemStatus[] | undefined,
        mode: SalesChartMode,
        xKey?: string,
        seriesKey?: string,
        yearCount?: number,
    ): SalesChartListRow[] {
        const now = new Date()
        const items = this.chartItemsForBar({
            groupBy,
            statuses,
            xKey,
            seriesKey,
            yearCount,
            now,
        })
        const rows = items.map(row => listRowFromChartItem(row, groupBy, statuses, seriesKey, now))
        if (mode !== 'company') {
            return rows.sort((a, b) => b.value - a.value)
        }
        const groups = new Map<string, SalesChartListRow>()
        rows.forEach(row => {
            const key = `c-${row.groupKey || row.customerName}`
            const existing = groups.get(key)
            if (!existing) {
                groups.set(key, {
                    ...row,
                    groupKey: key,
                    itemCount: 1,
                })
                return
            }
            existing.itemCount = (existing.itemCount || 1) + 1
            existing.headline = `${existing.itemCount} Projekte`
            existing.personName = ''
            existing.value += row.value
            if (existing.quarterLabel !== row.quarterLabel) {
                existing.quarterLabel = ''
            }
        })
        return Array.from(groups.values()).sort((a, b) => b.value - a.value)
    }

    buildTopPeople(yearCount = 3, limit = 8): SalesRankingRow[] {
        return this.buildRankings('person', true, yearCount, limit)
    }

    buildTopCustomers(weighted: boolean, yearCount = 3, limit = 8): SalesRankingRow[] {
        return this.buildRankings('customer', weighted, yearCount, limit)
    }

    buildFreshnessChart(statuses: GLItemStatus[], seriesKey = 'value', now = new Date()): SalesChartStack {
        if (!statuses || statuses.length === 0) {
            return emptyChartStack()
        }
        const rows = this.chartRowsForDisplay({ statuses, requireThreadDate: true })
        if (rows.length === 0) {
            return emptyChartStack()
        }
        const values = new Map<string, number>()
        SALES_FRESHNESS_BUCKETS.forEach(bucket => values.set(bucket.key, 0))
        rows.forEach(row => {
            const threadDate = chartRowThreadDate(row)
            if (!threadDate) {
                return
            }
            const weeks = (now.getTime() - threadDate.getTime()) / MS_PER_WEEK
            const key = freshnessKeyFromWeeks(weeks)
            values.set(key, (values.get(key) || 0) + weightedPotential(row.revenue_potential, row.revenue_probability))
        })
        const series: SalesChartSeries[] = [{
            key: seriesKey,
            label: 'Gewichtetes Potenzial',
            color: colorFromKey('freshness'),
            itemId: 0,
            customerId: 0,
            status: statuses[0],
            customerName: '',
            headline: '',
        }]
        const points: SalesChartPoint[] = SALES_FRESHNESS_BUCKETS.map(bucket => {
            const value = values.get(bucket.key) || 0
            return {
                xKey: bucket.key,
                label: bucket.label,
                total: value,
                [seriesKey]: value,
            }
        })
        return { points, series, quarters: [], filterOptions: [] }
    }

    chartFilterOptions(mode: SalesChartMode, status?: GLItemStatus, statuses?: GLItemStatus[]): SalesChartFilterOption[] {
        const rows = this.chartRowsForDisplay({ status, statuses, requireQuarter: true })
        const seen = new Map<string, string>()
        rows.forEach(row => {
            if (mode === 'company') {
                const id = String(row.customerId || 0)
                if (!seen.has(id)) {
                    seen.set(id, row.customerName || 'Ohne Firma')
                }
            } else {
                const id = String(row.id)
                if (!seen.has(id)) {
                    const company = row.customerName || 'Ohne Firma'
                    const headline = row.headline || '(ohne Titel)'
                    seen.set(id, `${company} – ${headline}`)
                }
            }
        })
        return Array.from(seen.entries())
            .map(([id, name]) => ({ id, name }))
            .sort((a, b) => a.name.localeCompare(b.name, 'de'))
    }

    private chartQuarterRaster(rows: GLItemChartRow[]): SalesChartQuarter[] {
        const now = new Date()
        let minYear = now.getFullYear()
        let minQ = 0
        let maxYear = now.getFullYear()
        let maxQ = 3
        let hasData = false

        rows.forEach(row => {
            const q = GLItemDTO.quarterFromStartdate(row.startdate)
            if (!q) {
                return
            }
            const qi = quarterIndex(q.quarter)
            if (!hasData) {
                minYear = q.year
                minQ = qi
                maxYear = q.year
                maxQ = qi
                hasData = true
                return
            }
            if (q.year < minYear || (q.year === minYear && qi < minQ)) {
                minYear = q.year
                minQ = qi
            }
            if (q.year > maxYear || (q.year === maxYear && qi > maxQ)) {
                maxYear = q.year
                maxQ = qi
            }
        })

        const quarters: SalesChartQuarter[] = []
        for (let year = minYear; year <= maxYear; year++) {
            QUARTERS.forEach((quarter, qi) => {
                if (year === minYear && qi < minQ) {
                    return
                }
                if (year === maxYear && qi > maxQ) {
                    return
                }
                quarters.push({
                    key: quarterKey(year, quarter),
                    year,
                    quarter,
                    label: quarter,
                })
            })
        }
        return quarters
    }

    private chartYearRasterFromHistory(rows: GLItemChartRow[], statuses: GLItemStatus[]): number[] {
        let minYear: number | null = null
        let maxYear: number | null = null
        rows.forEach(row => {
            const year = chartRowHistoryYear(row, statuses)
            if (year === null) {
                return
            }
            if (minYear === null || year < minYear) {
                minYear = year
            }
            if (maxYear === null || year > maxYear) {
                maxYear = year
            }
        })
        if (minYear === null || maxYear === null) {
            return []
        }
        const years: number[] = []
        for (let year = minYear; year <= maxYear; year++) {
            years.push(year)
        }
        return years
    }

    private inLastYears(row: GLItemChartRow, yearCount: number): boolean {
        const year = chartRowYear(row)
        if (year === null) {
            return false
        }
        const now = new Date().getFullYear()
        return year >= now - (yearCount - 1) && year <= now
    }

    private buildRankings(
        kind: 'person' | 'customer',
        weighted: boolean,
        yearCount: number,
        limit: number,
    ): SalesRankingRow[] {
        const totals = new Map<string, { name: string, value: number }>()
        this.chartItems.forEach(row => {
            if (!this.inLastYears(row, yearCount)) {
                return
            }
            const value = weighted
                ? weightedPotential(row.revenue_potential, row.revenue_probability)
                : (row.revenue_potential || 0)
            if (value <= 0) {
                return
            }
            const id = kind === 'person'
                ? String(row.personId || 0)
                : String(row.customerId || 0)
            const name = kind === 'person'
                ? (row.personName || 'Ohne Person')
                : (row.customerName || 'Ohne Firma')
            const current = totals.get(id) || { name, value: 0 }
            current.value += value
            if (name) {
                current.name = name
            }
            totals.set(id, current)
        })
        return Array.from(totals.entries())
            .map(([id, entry]) => ({ id, name: entry.name, value: entry.value }))
            .sort((a, b) => b.value - a.value)
            .slice(0, limit)
    }

    async setNeedle(needle: string) {
        this.items = []
        this.request.needle = needle
        await this.getFresh()
    }

    async createNew(data: IGLItem) {
        const newItem = new GLItemDTO(data)
        await newItem.save()
        await this.getFresh()
    }

    async deleteItem(id: number) {
        const item = this.items.find(i => i.id === id)
        if(item) {
            await item.delete()
            await this.getFresh()
        }
    }

    get() {
        let out: GLItemDTO[] = []
        if (this.request.id && this.request.id > -1) {
            // Detail view - do not show main item, only children:
            this.items.forEach(item => {
                if (item.children) {
                    for (let i = item.children.length -1; i >= 0; i--) {
                        const c = item.children[i]
                        out.push(c)
                    }
                }
            })
        } else {
            out = this.items
        }
        return out
    }

    getHeadlineDetails() {
        const item = this.items[0]
        if (!item) { return {
            headline: '',
            personName: '',
            customerName: '',
        } }
        return {
            headline: item.headline,
            personName: `${item.person?.firstname || ''} ${item.person?.lastname || ''}`,
            customerName: `${item.person?.customerName || ''}`,
        }
    }

    getHeadline1() {
        const details = this.getHeadlineDetails()
        return details.headline
    }

    getHeadline2() {
        const details = this.getHeadlineDetails()
        if (details.personName && details.customerName) {
            return `${details.personName} (${details.customerName})`
        }
    }
}
